import {
  EntityLinks,
  Goal,
  GoalImpactLevel,
  GoalProgressCount,
  GoalSortingMode,
  ID,
} from '@griply/types';

import { getGoalAchievedPercentage } from './get-goal-achieved-percentage';
import { buildGoalsById, getGoalLifeAreas } from './get-goal-life-area';
import { sortItems } from './sort-items';

export type SortGoalsOptions<T extends Goal = Goal> = {
  allGoals: T[];
  sortMode?: GoalSortingMode;
  customOrder?: ID[] | null;
  invertStartDateSorting?: boolean;
  lifeAreaOrder?: Map<ID, number>;
  // Tasks mode no-ops without this — never falls back to goal.taskCount.
  taskCounts?: Map<ID, number>;
  // Progress mode reads live task progress here — never from the stored counters.
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  links?: EntityLinks;
};

const compareByDeadline = (goalA: Goal, goalB: Goal): number => {
  const { deadline: deadlineA } = goalA;
  const { deadline: deadlineB } = goalB;
  if (!deadlineA && !deadlineB) {
    return 0;
  }
  if (!deadlineA) {
    return 1;
  }
  if (!deadlineB) {
    return -1;
  }
  return deadlineA.getTime() - deadlineB.getTime();
};

export const sortGoals = <T extends Goal>(
  goals: T[],
  {
    allGoals,
    sortMode,
    customOrder,
    invertStartDateSorting,
    lifeAreaOrder,
    taskCounts,
    goalProgressCounts,
    links,
  }: SortGoalsOptions<T>,
): T[] => {
  if (sortMode === GoalSortingMode.Custom) {
    return sortItems(goals, customOrder ?? [], 'id');
  }

  if (sortMode === GoalSortingMode.StartDate) {
    return goals.slice().sort((goalA, goalB) => {
      const { startDate: startDateA } = goalA;
      const { startDate: startDateB } = goalB;

      if (!startDateA && !startDateB) {
        return compareByDeadline(goalA, goalB);
      }
      if (!startDateA) {
        return 1;
      }
      if (!startDateB) {
        return -1;
      }

      const dateA = invertStartDateSorting ? startDateB : startDateA;
      const dateB = invertStartDateSorting ? startDateA : startDateB;
      const diff = dateA.getTime() - dateB.getTime();
      return diff !== 0 ? diff : compareByDeadline(goalA, goalB);
    });
  }

  if (sortMode === GoalSortingMode.Deadline) {
    return goals
      .slice()
      .sort(({ deadline: deadlineA }, { deadline: deadlineB }) => {
        if (!deadlineA && !deadlineB) {
          return 0;
        }
        if (!deadlineA) {
          return 1;
        }
        if (!deadlineB) {
          return -1;
        }

        return deadlineA.getTime() - deadlineB.getTime();
      });
  }

  if (sortMode === GoalSortingMode.Name) {
    return goals.slice().sort((goalA, goalB) => {
      const diff = goalA.name.localeCompare(goalB.name);
      return diff !== 0 ? diff : compareByDeadline(goalA, goalB);
    });
  }

  if (sortMode === GoalSortingMode.Tasks && taskCounts) {
    return goals.slice().sort((goalA, goalB) => {
      const tasksA = taskCounts.get(goalA.id) ?? 0;
      const tasksB = taskCounts.get(goalB.id) ?? 0;
      const diff = tasksB - tasksA;
      return diff !== 0 ? diff : compareByDeadline(goalA, goalB);
    });
  }

  if (sortMode === GoalSortingMode.Impact) {
    const ranking: Record<GoalImpactLevel, number> = {
      [GoalImpactLevel.High]: 0,
      [GoalImpactLevel.Medium]: 1,
      [GoalImpactLevel.Low]: 2,
    };
    return goals.slice().sort((goalA, goalB) => {
      const rankA = goalA.impact != null ? ranking[goalA.impact] : 3;
      const rankB = goalB.impact != null ? ranking[goalB.impact] : 3;
      const diff = rankA - rankB;
      return diff !== 0 ? diff : compareByDeadline(goalA, goalB);
    });
  }

  if (sortMode === GoalSortingMode.Progress) {
    return goals.slice().sort((goalA, goalB) => {
      const percentageA = getGoalAchievedPercentage(
        goalA,
        allGoals,
        goalProgressCounts,
      );
      const percentageB = getGoalAchievedPercentage(
        goalB,
        allGoals,
        goalProgressCounts,
      );

      // Goals without a measurable target sink below every measurable goal.
      if ((percentageA === undefined) !== (percentageB === undefined)) {
        return percentageA === undefined ? 1 : -1;
      }

      const diff = (percentageB ?? 0) - (percentageA ?? 0);
      return diff !== 0 ? diff : compareByDeadline(goalA, goalB);
    });
  }

  if (sortMode === GoalSortingMode.LifeArea && lifeAreaOrder) {
    const goalsById = buildGoalsById(allGoals);
    // A goal in several life areas orders by its earliest one, so the flat list stays stable.
    const earliestLifeAreaOrder = (goal: Goal): number =>
      getGoalLifeAreas(goal, { goalsById, links }).reduce(
        (best, lifeAreaId) =>
          Math.min(best, lifeAreaOrder.get(lifeAreaId) ?? Infinity),
        Infinity,
      );

    return goals.slice().sort((goalA, goalB) => {
      const orderA = earliestLifeAreaOrder(goalA);
      const orderB = earliestLifeAreaOrder(goalB);
      const diff = orderA - orderB;
      return diff !== 0 ? diff : compareByDeadline(goalA, goalB);
    });
  }

  return goals;
};
